/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, Input, ViewContainerRef, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { AbstractObject } from '@src/app/model/abstract-object';
import { PickLinkField, PickLinkOptions } from '@src/app/shared/field/PickLinkField';
import { AppFormControl, ControlOn } from '@src/app/shared/form/app-form-control';
import { FormControlInterface } from '@src/app/shared/form/form-control-interface';
import { DialogOptions, PickDialogComponent } from '@src/app/shared/dialogs/pick-dialog/pick-dialog.component';
import { Field } from '@src/app/shared/field/Field';
import { FormErrorComponent } from "../form-error/form-error.component";

export class PickLinkControl extends AppFormControl {
    field: PickLinkField;
}

@Component({
    selector: 'app-form-pick-link',
    templateUrl: './form-pick-link.component.html',
    styleUrls: ['./form-pick-link.component.scss'],
    imports: [MatIconModule, MatTooltipModule, RouterLink,
    MatFormFieldModule, MatInputModule, ReactiveFormsModule, FormErrorComponent]
})
export class FormPickLinkComponent implements FormControlInterface {
    private dialog = inject(MatDialog);

    @Input() control: PickLinkControl;
    @Input() on: ControlOn = 'form';

    /**
     * The nav, pick and refreshes options are assigned after construction rather than passed
     * through the Field constructor - initFromPartial() recurses into any plain object it is
     * given, which a NavRoute and a set of callbacks do not survive.
     */
    public static make(opts: PickLinkOptions, options: Partial<Field> = {}): PickLinkField {
        const field = new PickLinkField({ label: opts.label, value: opts.value }, options);
        field.textValue = opts.textValue;
        field.nav = opts.nav;
        field.navFilter = opts.navFilter ?? null;
        field.pick = opts.pick ?? null;
        field.refreshes = opts.refreshes ?? [];
        field.formControlFactory = FormPickLinkComponent.createComponent;
        return field;
    }

    public static createComponent(vcr: ViewContainerRef, ctl: PickLinkControl, on: ControlOn) {
        const componentRef = vcr.createComponent(FormPickLinkComponent);
        componentRef.instance.control = ctl;
        componentRef.instance.on = on;
        return componentRef;
    }

    get field(): PickLinkField {
        return this.control.field;
    }

    /** No pick options means a display only link - there is nothing to choose from */
    get editable(): boolean {
        return !!this.field.pick && !this.field.readonly && !this.field.disable;
    }

    get showClear(): boolean {
        return this.editable && this.field.pick.allowNull && !!this.control.value;
    }

    get chooseTip(): string {
        return this.control.value
            ? $localize`Click to change the ${this.field.label}`
            : $localize`Click to choose a ${this.field.label}`;
    }

    get clearTip(): string {
        return $localize`Click to remove the ${this.field.label}`;
    }

    /**
     * The candidate list is fetched here rather than up front, so a filter reading another field
     * sees the value that is on screen now.
     */
    openPicker() {
        const field = this.field;
        const item = field.getCurrentItem();

        const data: DialogOptions = {
            config: field.pick.config(),
            service: field.pick.service,
            showSearch: true,
            hideTabs: true,
            forceTeamId: field.getForceTeamId(item),
            listParms: field.getListParams(item),
        };

        this.dialog.open(PickDialogComponent, { data }).afterClosed().subscribe((row: AbstractObject) => {
            if (row) {
                field.setPicked(row);
            }
        });
    }

    clear() {
        this.field.clear();
    }
}
